// ── v2 template token grammar (shared by tokenizer + resolver) ────────────────
// See Docs/TEMPLATE_SYSTEM.md §3. Single-brace positional placeholders. The SAME
// token appears in id fields and name/title fields; resolution is field-typed
// (id refs handled via the slot descriptor, names via a global regex pass).

export type MetricCategory =
  | "gps" | "testing" | "forceplate" | "wellness" | "nutrition" | "cognition";

export type SubjectKind = "player" | "position" | "group";

/** Matches any v2 name/subject token, e.g. {MET_1}, {PL_2}, {POS_1}, {GRP_3}. */
export const TOKEN_RE = /\{(MET|PL|POS|GRP)_(\d+)\}/g;

export const metricToken = (n: number): string => `{MET_${n}}`;
export const subjectToken = (kind: SubjectKind, n: number): string =>
  `{${kind === "player" ? "PL" : kind === "position" ? "POS" : "GRP"}_${n}}`;

/** Id-field prefix for each subject kind (drills are never tokenized). */
export const SUBJECT_ID_PREFIX: Record<SubjectKind, string> = {
  player: "player-",
  position: "pos-",
  group: "group-",
};

/** Manifest entry for a metric slot: identity (key/name) drives identity-keep;
 *  category scopes substitution and gates ACWR. */
export interface MetricSlot {
  category: MetricCategory;
  key: string;   // exporting team's unique_identifier — for identity-keep-first
  name: string;  // exporting team's display name — advisory
}

export interface SubjectSlot {
  kind: SubjectKind;
  name?: string; // advisory only; import resolves positionally
}

export interface TemplateSlots {
  metrics: Record<string, MetricSlot>;   // keyed by "MET_1", "MET_2", …
  subjects: Record<string, SubjectSlot>; // keyed by "PL_1", "POS_1", "GRP_1", …
}

/** v2 template file (on disk as <folder>/template.json). Widgets keep the backend
 *  `config` key; queries are omitted and rebuilt from spec on save. */
export interface TemplateFileV2 {
  spec_version: 2;
  id: string;
  name: string;
  widgets: Array<{ id: string; type: string; config: Record<string, unknown> }>;
  layout: Array<{ i: string; x: number; y: number; w: number; h: number }>;
  slots: TemplateSlots;
}

/** Per-team inputs the resolver substitutes from. */
export interface TemplateResolveContext {
  metricsByCategory: Record<MetricCategory, Array<{ id: string; name: string }>>;
  subjects: {
    player: Array<{ id: string; name: string }>;
    position: Array<{ id: string; name: string }>;
    group: Array<{ id: string; name: string }>;
  };
  dashboardName: string;
}

export interface ResolutionReport {
  substitutions: string[]; // human-readable lines for the post-import summary
  droppedWidgetIds: string[];
}

/** Parse a token string into its kind + index, or null if not a token. */
export function parseToken(
  tok: string,
): { kind: "MET" | "PL" | "POS" | "GRP"; n: number } | null {
  const m = /^\{(MET|PL|POS|GRP)_(\d+)\}$/.exec(tok);
  return m ? { kind: m[1] as "MET" | "PL" | "POS" | "GRP", n: Number(m[2]) } : null;
}

export const SUBJECT_KIND_BY_PREFIX: Record<"PL" | "POS" | "GRP", SubjectKind> = {
  PL: "player",
  POS: "position",
  GRP: "group",
};
